import React, { useEffect, useState } from 'react';
import { Card, CardContent, Typography, Grid, Box, Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableHead, TableRow, TableCell, TableBody, TableSortLabel, FormControl, InputLabel, Select, MenuItem, InputAdornment, CircularProgress, Divider } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import BuildIcon from '@mui/icons-material/Build';
import EuroIcon from '@mui/icons-material/Euro';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import RedeemIcon from '@mui/icons-material/Redeem';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { lifecyclePhases, getColorForPhase } from '../../helpers/utils';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

const SummaryTwo = ({ global }) => {
    const [open, setOpen] = useState(false);
    const [popupContent, setPopupContent] = useState({ title: '', companies: [] });
    const [selectedLifecycle, setSelectedLifecycle] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
    const [activesSubscriptions, setActivesSubscriptions] = useState([]);
    const [pausedSubscriptions, setPausedSubscriptions] = useState([]);
    const [caProducts, setCaProducts] = useState([]);
    const [caTotal, setCaTotal] = useState([]);


    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        if (Array.isArray(global?.allInvoices?.allInvoices)) {  // 🔹 Vérification du tableau
            const activesSubscriptions = global.allInvoices.allInvoices.filter(
                invoice => invoice.fbVisState === 0 && invoice.fbInvoiceType === 'Abonnement'
            );
            setActivesSubscriptions(activesSubscriptions);

            const pausedSubscriptions = global.allInvoices.allInvoices.filter(
                invoice => (invoice.fbVisState === 1 || invoice.fbVisState === 2) && invoice.fbInvoiceType === 'Abonnement'
            );
            setPausedSubscriptions(pausedSubscriptions);

            const caProducts = global.allInvoices.allInvoices.filter(
                invoice => invoice.fbInvoiceType === 'Produit'
            );
            setCaProducts(caProducts);

            const caTotal = global.allInvoices.allInvoices.filter(
                invoice => invoice.fbInvoiceType === 'Produit' || invoice.fbInvoiceType === 'Service'
            );
            setCaTotal(caTotal);

        }
    }, [global]);

    const sortByDate = (companies) => {
        if (!Array.isArray(companies)) {
            return [];
        }
        return companies.slice().sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    };

    const handleSort = (key) => {
        setSortConfig((prevConfig) => ({
            key,
            direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc',
        }));
    };

    const calculateTotalHT = (invoices) => {
        const total = invoices.reduce((sum, invoice) => {
            const fbProductsDetails = invoice.fbProductsDetails ? JSON.parse(invoice.fbProductsDetails) : null;
            if (fbProductsDetails && fbProductsDetails.cost) {
                const totalHT = parseFloat(fbProductsDetails.cost.totalHT) || 0;
                const discountValue = parseFloat(fbProductsDetails.cost.discountValue) || 0;
                return sum + (totalHT - discountValue);
            }
            return sum;
        }, 0);

        return total === 0 ? '0' : total.toFixed(2);
    };


    const totalHTActivesSubscriptions = calculateTotalHT(activesSubscriptions);
    const totalHTPausedSubscriptions = calculateTotalHT(pausedSubscriptions);
    const totalHTCaProducts = calculateTotalHT(caProducts);
    const totalHTCaTotal = calculateTotalHT(caTotal);


    const dashboardData = [
        {
            icon: <WorkspacePremiumIcon style={{ fontSize: 40, color: '#2196F3' }} />,
            title: 'Clients',
            value: global?.clientsFremiums?.clients.length,
            footer: 'Clients freemium',
            companies: global?.clientsFremiums?.clients,
            footerStyle: { color: '#d32f2f' },
        },
        {
            icon: <VerifiedUserIcon style={{ fontSize: 40, color: '#4CAF50' }} />,
            title: `${activesSubscriptions.length} - Abonnements actifs`,
            value: `${totalHTActivesSubscriptions} €`,
            companies: sortByDate(activesSubscriptions),
            footer: 'Abonnements en cours',
        },
        {
            icon: <CancelIcon style={{ fontSize: 40, color: '#F44336' }} />,
            title: `${pausedSubscriptions.length} - Churns`,
            value: `${totalHTPausedSubscriptions} €`,
            companies: sortByDate(pausedSubscriptions),
            footer: 'Abonnements en pause ou supprimés',
        },
        {
            icon: <EuroIcon style={{ fontSize: 40, color: '#FF9800' }} />,
            title: `${caProducts.length} - CA produits`,
            value: `${totalHTCaProducts} €`,
            companies: sortByDate(caProducts),
            footer: 'Factures créées de produit ',
        },
        {
            icon: <EuroIcon style={{ fontSize: 40, color: '#9370DB' }} />,
            title: `${caTotal.length} - CA total`,
            value: `${totalHTCaTotal} €`,
            companies: sortByDate(caTotal),
            footer: 'Factures produit et abonnements',
        },
    ];

    const handleFooterClick = (title, companies) => {
        setPopupContent({ title, companies });
        setOpen(true);
    };

    const handleRedirectToCompany = (companyId) => {
        const baseUrl = window.location.origin;
        const url = `${baseUrl}/home?q=${companyId}`;
        window.open(url, '_blank');
    };

    const getLifecycleLabel = (value) => {
        const phase = lifecyclePhases.find(phase => phase.value === value);
        return phase ? phase.label : "";
    };

    const handleClose = () => {
        setOpen(false);
    };

    const sortedCompanies = [...popupContent?.companies]
        .filter((company) =>
            selectedLifecycle ? company.previous_lifecyclestage === selectedLifecycle : true
        )
        .sort((a, b) => {
            if (!sortConfig.key) return 0;

            let aValue = a[sortConfig.key]?.toLowerCase();
            let bValue = b[sortConfig.key]?.toLowerCase();

            if (sortConfig.key === 'previous_lifecyclestage') {
                aValue = getLifecycleLabel(aValue);
                bValue = getLifecycleLabel(bValue);
            }
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });

    return (
        <Box pt={2} pb={2}>
            <Grid container spacing={3}>
                {dashboardData.map((data, index) => (
                    <Grid item xs={12} sm={6} md={2.4} key={index}>
                        <Card>
                            <Box display="flex" alignItems="center" p={2}>
                                <Box mr={2}>{data.icon}</Box>
                                <CardContent>
                                    <Typography variant="subtitle2" color="textSecondary" pb={1.2}>
                                        {data.title}
                                    </Typography>
                                    <Typography variant="h5" fontWeight="bold">
                                        {data.value}
                                    </Typography>
                                    <Typography
                                        pt={1.4}
                                        variant="body1"
                                        sx={{
                                            ...data.footerStyle,
                                            fontSize: '.6rem',
                                            cursor: 'pointer',
                                            whiteSpace: 'nowrap',     // Garde le texte sur une seule ligne
                                            overflow: 'hidden',      // Cache tout ce qui déborde
                                            textOverflow: 'ellipsis', // Ajoute "..." si le texte est trop long
                                            display: 'block',         // Assure que les styles fonctionnent
                                            width: '100%',            // S'assure que le conteneur a une largeur définie
                                        }}
                                        onClick={() => handleFooterClick(data.footer, data.companies)}
                                    >
                                        {data.footer}
                                    </Typography>
                                </CardContent>
                            </Box>
                        </Card>
                    </Grid>
                ))}
            </Grid>
            <Dialog open={open} onClose={handleClose} sx={{
                '& .MuiDialog-paper': {
                    width: isMobile ? '96%' : 600,
                    margin: isMobile ? 2 : 'auto',

                    height: isMobile ? 'calc(100vh - 200px)' : 650,
                },
            }}>
                <DialogTitle color='warning' sx={{ padding: 2, textAlign: 'center', fontWeight: 600 }}>{popupContent.title}</DialogTitle>

                <DialogContent sx={{ padding: 2 }}>
                    <Table>
                        {popupContent.title === 'Clients freemium' ? (
                            <>
                                {/* Tableau des entreprises */}
                                <Table>
                                    <TableHead>
                                        {popupContent.companies.length > 0 && (
                                            <TableRow>
                                                <TableCell sx={{ fontWeight: 600, padding: 1 }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'owner'}
                                                        direction={sortConfig.key === 'owner' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('owner')}
                                                    >
                                                        ENTREPRISE
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, padding: 1 }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'previous_lifecyclestage'}
                                                        direction={sortConfig.key === 'previous_lifecyclestage' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('previous_lifecyclestage')}
                                                    >
                                                        PROSPECTEUR
                                                    </TableSortLabel>
                                                </TableCell>
                                                <TableCell sx={{ fontWeight: 600, padding: 1 }}>
                                                    <TableSortLabel
                                                        active={sortConfig.key === 'createdAt'}
                                                        direction={sortConfig.key === 'createdAt' ? sortConfig.direction : 'asc'}
                                                        onClick={() => handleSort('createdAt')}
                                                    >
                                                        DATE ET HEURE
                                                    </TableSortLabel>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableHead>
                                    <TableBody>
                                        {sortedCompanies.map((company, index) => (
                                            <TableRow key={`${company.id}-${index}`}>
                                                <TableCell
                                                    onClick={() => handleRedirectToCompany(company.id)}
                                                    sx={{ padding: 1, cursor: 'pointer', color: '#4caf50' }}
                                                >
                                                    {company.name}

                                                </TableCell>
                                                <TableCell sx={{ padding: 1 }}>
                                                    {company.owner}
                                                </TableCell>
                                                <TableCell sx={{ padding: 1 }}>
                                                    {new Date(company.createdAt).toLocaleString('fr-FR', {
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                    })}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {/* Gestion de l'affichage si aucune entreprise ne correspond au filtre */}
                                        {sortedCompanies.length === 0 && (
                                            <TableRow>
                                                <TableCell colSpan={3} align="center">
                                                    <Typography>Pas de détails</Typography>
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </>
                        ) : (
                            <>
                                <TableHead>
                                    {popupContent?.companies.length > 0 && (
                                        <TableRow>
                                            <TableCell sx={{ fontWeight: 600, padding: 1 }}>ENTREPRISE</TableCell>
                                            <TableCell sx={{ fontWeight: 600, padding: 1 }}> TYPE </TableCell>
                                            <TableCell sx={{ fontWeight: 600, padding: 1 }}> PRIX HT </TableCell>
                                            <TableCell sx={{ fontWeight: 600, padding: 1 }}>DATE</TableCell>
                                        </TableRow>
                                    )}
                                </TableHead>
                                <TableBody>
                                    {popupContent?.companies.length > 0 ? (
                                        popupContent?.companies.map((invoice) => (
                                            <TableRow key={invoice.id}>
                                                <TableCell onClick={() => handleRedirectToCompany(invoice.fbHubspotCompanyId)} sx={{ padding: 1, cursor: 'pointer', color: '#4caf50' }}>
                                                    {invoice.fbCompanyName}
                                                    <Typography variant="body2" sx={{ fontStyle: 'italic', color: 'gray' }}>
                                                        {invoice.fbHubspotOwner}
                                                    </Typography>
                                                </TableCell>
                                                <TableCell sx={{ padding: 1 }}>
                                                    {invoice.fbProductsDetails && JSON.parse(invoice.fbProductsDetails)?.products
                                                        ? JSON.parse(invoice.fbProductsDetails).products.map((product, index) => (
                                                            <div key={index}>
                                                                - {product.name}
                                                            </div>
                                                        ))
                                                        : 'Aucun produit'}
                                                </TableCell>
                                                <TableCell sx={{ padding: 1 }}>
                                                    {
                                                        JSON.parse(invoice.fbProductsDetails)?.cost?.totalHT
                                                            ? (parseFloat(JSON.parse(invoice.fbProductsDetails)?.cost?.totalHT) - parseFloat(JSON.parse(invoice.fbProductsDetails)?.cost?.discountValue || 0)).toFixed(2)
                                                            : 'N/A'
                                                    } € 
                                                </TableCell>

                                                <TableCell sx={{ padding: 1 }}>
                                                    {new Date(invoice.createdAt).toLocaleString('fr-FR', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                    })}
                                                </TableCell>
                                            </TableRow>
                                        ))
                                    ) : (
                                        <TableRow>
                                            <TableCell colSpan={3} align="center">
                                                <Typography>Pas de détails</Typography>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </>
                        )}
                    </Table >

                </DialogContent >
                <DialogActions style={{ justifyContent: 'center', marginBottom: 20 }}>
                    <Button onClick={handleClose} color="warning" variant="outlined" style={{ width: 150 }}>
                        Fermer
                    </Button>
                </DialogActions>
            </Dialog >
        </Box >
    );
};

export default SummaryTwo;
